import type { BillingCycle, ItemMeta } from './types.js';

/**
 * Statische Umrechnungskurse auf die Basiswaehrung.
 * Bewusst fest verdrahtet: Live-Kurse waeren fuer eine Kostenuebersicht
 * mehr Aufwand als Nutzen. Bei Bedarf hier anpassen.
 */
export const BASE_CURRENCY = 'EUR';

export const FX_TO_BASE: Record<string, number> = {
  EUR: 1,
  USD: 0.92,
  GBP: 1.17,
  CHF: 1.05,
};

export const CURRENCIES = Object.keys(FX_TO_BASE);

export const CYCLE_LABEL: Record<BillingCycle, string> = {
  monthly: 'monatlich',
  quarterly: 'quartalsweise',
  yearly: 'jährlich',
  weekly: 'wöchentlich',
  onetime: 'einmalig',
  free: 'kostenlos',
};

/** Wie viele Zahlungen dieses Zyklus fallen pro Monat an? */
export function cycleMonthlyFactor(cycle: BillingCycle): number {
  switch (cycle) {
    case 'monthly':
      return 1;
    case 'quarterly':
      return 1 / 3;
    case 'yearly':
      return 1 / 12;
    case 'weekly':
      return 52 / 12;
    case 'onetime':
    case 'free':
      return 0;
  }
}

export function toBaseCents(cents: number, currency: string): number {
  const rate = FX_TO_BASE[currency] ?? 1;
  return Math.round(cents * rate);
}

type Priced = Pick<
  ItemMeta,
  'priceCents' | 'cycle' | 'currency' | 'status' | 'priceIsPerSeat' | 'seats'
> & { hasCost?: boolean; priceIsNet?: boolean; vatRatePercent?: number };

/** Ein Betrag in seinen drei Ausprägungen. Alles in Cent der Basiswaehrung. */
export interface Money {
  netCents: number;
  vatCents: number;
  grossCents: number;
}

export const ZERO_MONEY: Money = { netCents: 0, vatCents: 0, grossCents: 0 };

export function addMoney(a: Money, b: Money): Money {
  return {
    netCents: a.netCents + b.netCents,
    vatCents: a.vatCents + b.vatCents,
    grossCents: a.grossCents + b.grossCents,
  };
}

export function sumMoney(items: Money[]): Money {
  return items.reduce(addMoney, ZERO_MONEY);
}

/** Wie oft im Jahr dieser Zyklus abgerechnet wird. */
export function periodsPerYear(cycle: BillingCycle): number {
  switch (cycle) {
    case 'monthly':
      return 12;
    case 'quarterly':
      return 4;
    case 'yearly':
      return 1;
    case 'weekly':
      return 52;
    case 'onetime':
    case 'free':
      return 0;
  }
}

/** Zerlegt einen Betrag je nachdem, ob er netto oder brutto eingetragen wurde. */
export function splitVat(amountCents: number, item: Priced): Money {
  const rate = (item.vatRatePercent ?? 0) / 100;
  if (item.priceIsNet === false) {
    const netCents = Math.round(amountCents / (1 + rate));
    return { netCents, vatCents: amountCents - netCents, grossCents: amountCents };
  }
  const vatCents = Math.round(amountCents * rate);
  return { netCents: amountCents, vatCents, grossCents: amountCents + vatCents };
}

/** Zugaenge ohne Vertrag und gekuendigte Vertraege belasten nichts. */
function costsNothing(item: Priced): boolean {
  return item.hasCost === false || item.status === 'cancelled';
}

/**
 * Preis einer Abrechnungsperiode in der Basiswaehrung.
 * Bei Preisen je Platz wird mit der Zahl der *bezahlten* Plaetze multipliziert -
 * bezahlt wird schliesslich auch fuer unbesetzte.
 */
export function periodBaseCents(item: Priced): number {
  const seats = item.priceIsPerSeat ? Math.max(1, item.seats ?? 1) : 1;
  return toBaseCents(item.priceCents * seats, item.currency);
}

/**
 * Was bei einer Abrechnung tatsaechlich abgebucht wird - ohne Umrechnung auf
 * andere Zeitraeume. Ein Jahresvertrag liefert hier den Jahresbetrag.
 */
export function periodMoney(item: Priced): Money {
  if (costsNothing(item)) return ZERO_MONEY;
  return splitVat(periodBaseCents(item), item);
}

/** Was der Vertrag ueber ein Jahr kostet, egal in welchem Rhythmus er abgerechnet wird. */
export function annualMoney(item: Priced): Money {
  if (costsNothing(item)) return ZERO_MONEY;
  const perYear = periodsPerYear(item.cycle);
  if (perYear === 0) return ZERO_MONEY;
  return splitVat(periodBaseCents(item) * perYear, item);
}

/**
 * Auf den Monat umgelegt. Nur zur Einordnung eines einzelnen Vertrags gedacht -
 * fuer die Frage "was geht diesen Monat vom Konto ab" ist `periodMoney` richtig,
 * gefiltert nach dem Abrechnungsrhythmus.
 */
export function monthlyBaseCents(item: Priced): number {
  if (costsNothing(item)) return 0;
  return Math.round(periodBaseCents(item) * cycleMonthlyFactor(item.cycle));
}

export function yearlyBaseCents(item: Priced): number {
  return monthlyBaseCents(item) * 12;
}

/**
 * Einmalige Anschaffung. Faellt bewusst nicht in die Monatsrechnung -
 * eine gekaufte Lizenz ist kein laufender Posten.
 */
export function oneTimeBaseCents(item: Priced): number {
  if (costsNothing(item) || item.cycle !== 'onetime') return 0;
  return periodBaseCents(item);
}

export function oneTimeMoney(item: Priced): Money {
  if (costsNothing(item) || item.cycle !== 'onetime') return ZERO_MONEY;
  return splitVat(periodBaseCents(item), item);
}

/** Uebliche Saetze. 0 % deckt Reverse-Charge-Rechnungen aus dem Ausland ab. */
export const VAT_RATES = [19, 7, 0];

export function vatLabel(rate: number): string {
  if (rate === 0) return '0 % (z. B. Reverse Charge)';
  return `${rate} %`;
}

export function formatMoney(cents: number, currency = BASE_CURRENCY, locale = 'de-DE'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

/** "12,90" -> 1290. Akzeptiert Komma und Punkt. */
export function parseMoneyToCents(input: string): number {
  const cleaned = input.replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}\b)/g, '').replace(',', '.');
  const value = Number.parseFloat(cleaned);
  return Number.isFinite(value) ? Math.round(value * 100) : 0;
}

/** Tage bis zu einem ISO-Datum. Negativ, wenn es in der Vergangenheit liegt. */
export function daysUntil(isoDate: string | null, now = new Date()): number | null {
  if (!isoDate) return null;
  const target = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - startOfToday.getTime()) / 86_400_000);
}
